import {createUniversalFetch, navigateTo, useAuth, useLocale, useLocalePrefix, useLogger, useRuntimeConfig, type Language} from '#imports';
import {useFeatureFlag} from '@malt/feature-flags';
import {withForcedLocalePrefix} from '../utils/localeSelection';

const universalFetch = createUniversalFetch({baseURL: '/navbar'});

export function useLocalizationSelection() {
    const logger = useLogger();
    const {loggedIn} = useAuth();
    const {locale} = useLocale();
    const runtimeConfig = useRuntimeConfig();
    const {getLocaleCodeFromPrefix, getLocalePrefix, withoutLocalePrefix} = useLocalePrefix();
    const isLocalePrefixInLanguageSelectorEnabled = useFeatureFlag('locale-prefix-in-language-selector');

    // Get the current locale from the URL or the browser's locale
    function getCurrentLocale(): string {
        const localePrefix = getLocalePrefix();
        const localeFromPrefix = localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined;

        return localeFromPrefix ?? locale.value;
    }

    async function changeLanguageAndRedirect(selectedLanguage: Language) {
        if (!selectedLanguage.host) {
            return;
        }

        const targetUrl = _resolveTargetUrl(selectedLanguage.host, selectedLanguage.locale);
        const hostChanged = targetUrl.host !== window.location.host;

        if (loggedIn && hostChanged) {
            await _changeLanguageAndRedirectForLoggedInUser(targetUrl);
        } else {
            navigateTo(targetUrl.toString(), {external: true});
        }
    }

    function _resolveTargetUrl(newHost: string, locale?: string): URL {
        const url = new URL(window.location.href);
        url.searchParams.delete('lg');

        // Check if the locale prefix is supported
        const {supportedLocalePrefixes} = runtimeConfig.public['@malt/nuxt-i18n-module'];
        const maybeLocalePrefix = locale?.replace('_', '-').toLowerCase(); // en_GB -> en-gb
        const isSupportedLocalePrefix = (supportedLocalePrefixes as {prefix: string}[] | undefined)?.some(({prefix}) => prefix === maybeLocalePrefix);
        const shouldRedirectToMultiLocaleHost = locale && isLocalePrefixInLanguageSelectorEnabled.value && isSupportedLocalePrefix;

        // Build redirect url
        if (shouldRedirectToMultiLocaleHost && maybeLocalePrefix) {
            url.host = toComHost(newHost);
            // withLocalePrefix is a no-op on country hosts (malt.de, malt.fr); always prefix the .com destination
            const strippedPathname = withoutLocalePrefix(url.pathname) as string;
            url.pathname = withForcedLocalePrefix(strippedPathname, maybeLocalePrefix);
        } else {
            // Remove any existing locale prefix when redirecting to a classic domain
            url.pathname = withoutLocalePrefix(url.pathname) as string;
            url.host = newHost;
        }

        return url;
    }

    // @XAuthTours(step = tour_send_redirect, includeCode = true)
    async function _changeLanguageAndRedirectForLoggedInUser(targetUrl: URL): Promise<void> {
        // lib-front-shared-domains/nuxt-navbar-module/src/runtime/logged/features/language-selector/language-selector.store.ts
        try {
            const userToken = await universalFetch<string>('/api/user/x-auth-token');
            await universalFetch('/api/user/unsudosu', {method: 'POST'});

            const xAuthUrl = new URL(`${window.location.protocol}//${targetUrl.host}/navbar/api/xauth`);
            xAuthUrl.searchParams.append('redirectUrl', targetUrl.toString());
            xAuthUrl.searchParams.append('x-auth', userToken);

            navigateTo(xAuthUrl.toString(), {external: true});
        } catch (e) {
            logger.error("Can't change language", e);
        }
    }

    // e.g. www.malt.fr → www.malt.com, deven.malt.fr → dev.malt.com
    function toComHost(newHost: string): string {
        const parts = newHost.split('.');
        if (parts[0]?.startsWith('dev')) {
            parts[0] = 'dev';
        }
        parts[parts.length - 1] = 'com';
        return parts.join('.');
    }

    return {
        // functions A-Z
        changeLanguageAndRedirect,
        getCurrentLocale,
        // refs A-Z
    };
}
